import { type SupabaseClient, FunctionsFetchError, FunctionsHttpError } from '@supabase/supabase-js';

const FUNCTION_NAME = 'scrape-forum-stats';

const SCRAPER_PROXY_SKIP = '__SCRAPER_PROXY_SKIP__';

export type ScrapeForumStatsPayload = {
  url: string;
  topics_selector?: string | null;
  posts_selector?: string | null;
  members_selector?: string | null;
  scrape_login_url?: string | null;
  scrape_username?: string | null;
  scrape_password?: string | null;
  scrape_login_username_selector?: string | null;
  scrape_login_password_selector?: string | null;
  scrape_login_submit_selector?: string | null;
};

export type ScrapeLoginFields = {
  scrape_login_url?: string | null;
  scrape_username?: string | null;
  scrape_password?: string | null;
  scrape_login_username_selector?: string | null;
  scrape_login_password_selector?: string | null;
  scrape_login_submit_selector?: string | null;
};

/** Forum kaydı veya formdan scrape isteği gövdesi oluşturur. */
export function buildScrapeForumStatsPayload(
  source: {
    url?: string | null;
    topics_selector?: string | null;
    posts_selector?: string | null;
    members_selector?: string | null;
  } & ScrapeLoginFields
): ScrapeForumStatsPayload {
  const payload: ScrapeForumStatsPayload = {
    url: (source.url ?? '').trim(),
    topics_selector: source.topics_selector || null,
    posts_selector: source.posts_selector || null,
    members_selector: source.members_selector || null,
  };

  const loginUrl = source.scrape_login_url?.trim();
  const username = source.scrape_username?.trim();
  const password = source.scrape_password ?? '';
  const userSel = source.scrape_login_username_selector?.trim();
  const passSel = source.scrape_login_password_selector?.trim();
  const submitSel = source.scrape_login_submit_selector?.trim();

  if (loginUrl && username && password && userSel && passSel && submitSel) {
    payload.scrape_login_url = loginUrl;
    payload.scrape_username = username;
    payload.scrape_password = password;
    payload.scrape_login_username_selector = userSel;
    payload.scrape_login_password_selector = passSel;
    payload.scrape_login_submit_selector = submitSel;
  }

  return payload;
}

export type ScrapeForumStatsResult = {
  topics_count: number | null;
  posts_count: number | null;
  members_count: number | null;
  messages?: string[];
  error?: string;
};

/** Proxy/Edge yanıtında sayılar bazen string gelir; form + karşılaştırmalar için tek tipe çevir. */
export function normalizeScrapeCount(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return Math.trunc(value);
  if (typeof value === 'string') {
    const t = value.replace(/\u00a0/g, ' ').trim();
    const digits = t.replace(/[^\d]/g, '');
    if (digits === '') return null;
    const n = parseInt(digits, 10);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function finalizeScrapeResult(raw: ScrapeForumStatsResult & { error?: string }): ScrapeForumStatsResult {
  return {
    topics_count: normalizeScrapeCount(raw.topics_count),
    posts_count: normalizeScrapeCount(raw.posts_count),
    members_count: normalizeScrapeCount(raw.members_count),
    messages: Array.isArray(raw.messages) ? raw.messages : undefined,
    error: typeof raw.error === 'string' ? raw.error : undefined,
  };
}

function unwrapFetchFailure(context: unknown): string | null {
  if (context == null) return null;
  if (typeof context === 'string') return context;
  if (context instanceof Error) {
    const cause =
      'cause' in context && (context as { cause?: unknown }).cause instanceof Error
        ? ((context as { cause: Error }).cause.message)
        : null;
    const parts = [context.message, cause].filter(Boolean) as string[];
    return parts.join(' — ') || null;
  }
  if (typeof context === 'object' && 'message' in context && typeof (context as { message: unknown }).message === 'string') {
    return (context as { message: string }).message;
  }
  return null;
}

/** Vite base: / veya /alt/ veya ./ */
function normalizedAppBasePath(): string {
  const b = import.meta.env.BASE_URL || '/';
  if (b === '/' || b === './') return '';
  const withSlash = b.endsWith('/') ? b : `${b}/`;
  return withSlash.startsWith('/') ? withSlash : `/${withSlash}`;
}

const EDGE_DEPLOY_CMD = 'supabase login && npm run deploy:scrape-edge';

function formatEdgeNetworkHint(): string {
  return `Edge fonksiyonu yayında olmalı (${EDGE_DEPLOY_CMD}). config.toml: [functions.scrape-forum-stats] verify_jwt = false.`;
}

function isEdgeNotDeployedMessage(text: string): boolean {
  const t = text.toLowerCase();
  return (
    t.includes('not_found') ||
    t.includes('was not found') ||
    t.includes('requested function was not found') ||
    t.includes('edge_deploy_required') ||
    t.includes('scrape-forum-stats yayında değil')
  );
}

function formatEdgeNotDeployedError(prefix: string): string {
  return `${prefix}\n\nEdge henüz deploy edilmemiş olabilir. Terminalde:\n  ${EDGE_DEPLOY_CMD}`;
}

function simplifyEdgeFetchErrorMessage(full: string): string {
  const lines = full.split('\n').map((l) => l.trim()).filter(Boolean);
  const first = lines[0] ?? full;
  if (lines.length <= 2) {
    return [first, formatEdgeNetworkHint()].filter(Boolean).join('\n');
  }
  return `${first}\n${formatEdgeNetworkHint()}`;
}

function formatFinalScrapeFailure(
  edgeErrorMessage: string,
  proxyLastError: Error | null,
  proxyTried: boolean
): string {
  const blocks: string[] = [];

  if (proxyLastError?.message === SCRAPER_PROXY_SKIP) {
    blocks.push(
      [
        '1) PHP proxy (öncelik): Dosya 404 veya SPA HTML dönüyor.',
        '   → index.html ile aynı köke scrape-forum-proxy.php + forum-stats-bridge.php yükleyin.',
        '   → .htaccess: .php istekleri index.html rewrite’a düşmesin.',
        '   Düzelince istek sunucudan Edge’e gider (tam CSS, tarayıcı CORS’u yok sayılır).',
      ].join('\n')
    );
  } else if (proxyLastError) {
    blocks.push(`1) PHP proxy:\n${formatProxyFailureForUser(proxyLastError)}`);
  } else if (!proxyTried) {
    blocks.push('1) PHP proxy adayı yok (geliştirmede VITE_SCRAPE_FAILOVER_PROXY veya üretim build).');
  }

  const edgeBlock = isEdgeNotDeployedMessage(edgeErrorMessage)
    ? formatEdgeNotDeployedError('2) Tarayıcı Edge yedeği: fonksiyon bulunamadı (404).')
    : ['2) Tarayıcı Edge yedeği (şu an başarısız):', simplifyEdgeFetchErrorMessage(edgeErrorMessage)].join('\n');
  blocks.push(edgeBlock);

  blocks.push(`3) ${scrapeFailureChecklist(proxyTried, proxyLastError?.message ?? edgeErrorMessage)}`);

  return blocks.join('\n\n');
}

/**
 * proxyConfig: tam URL, /kök/yol.php, göreli yol veya scrape-forum-proxy.php
 */
function resolveProxyUrl(proxyConfig: string): string {
  const raw = proxyConfig.trim();
  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    return raw;
  }
  if (typeof window === 'undefined') {
    return raw.startsWith('/') ? `http://localhost${raw}` : `http://localhost/${raw}`;
  }

  if (!raw.startsWith('/')) {
    try {
      return new URL(raw, window.location.href).href;
    } catch {
      return `${window.location.origin}/${raw}`;
    }
  }

  const appBase = normalizedAppBasePath();
  if (appBase) {
    const pathNoLead = raw.replace(/^\//, '');
    try {
      return new URL(`${appBase}${pathNoLead}`, window.location.origin).href;
    } catch {
      return `${window.location.origin}${appBase}${pathNoLead}`;
    }
  }

  try {
    return new URL(raw, window.location.origin).href;
  } catch {
    return `${window.location.origin}${raw}`;
  }
}

function isProxyFetchSameOrigin(resolvedUrl: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URL(resolvedUrl).origin === window.location.origin;
  } catch {
    return false;
  }
}

function isRetryableProxyError(e: unknown): boolean {
  if (!(e instanceof Error)) return false;
  const m = e.message;
  return (
    m === SCRAPER_PROXY_SKIP ||
    m.includes('Failed to fetch') ||
    m.includes('NetworkError') ||
    m.includes('Load failed') ||
    m.includes('Proxy isteği gönderilemedi')
  );
}

function isTargetSiteFetchError(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes('http 403') ||
    lower.includes('forbidden') ||
    lower.includes('sayfa alınamadı') ||
    lower.includes('certificate') ||
    lower.includes('unknownissuer') ||
    lower.includes('invalid peer') ||
    lower.includes('ssl') ||
    lower.includes('bot/waf') ||
    lower.includes('hosting (php)')
  );
}

/** İç sentinel kullanıcıya gösterilmez */
function formatProxyFailureForUser(err: Error): string {
  if (err.message === SCRAPER_PROXY_SKIP) {
    return 'PHP proxy yanıt vermedi (404, HTML/SPA yanıtı veya dosya yok). Kök dizinde scrape-forum-proxy.php + forum-stats-bridge.php ve .htaccess’te .php için rewrite dışı bırakma kontrol edin.';
  }
  return err.message;
}

/** fetchWithAuth + doğru başlıklar; CORS Edge fonksiyonunda (OPTIONS + Allow-Headers) */
async function invokeScrapeViaEdgeFetch(
  supabase: SupabaseClient,
  payload: ScrapeForumStatsPayload
): Promise<ScrapeForumStatsResult> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Bu işlem için giriş yapmalısınız');
  }
  if (!import.meta.env.VITE_SUPABASE_URL?.trim() || !import.meta.env.VITE_SUPABASE_ANON_KEY?.trim()) {
    throw new Error('VITE_SUPABASE_URL veya VITE_SUPABASE_ANON_KEY eksik');
  }

  const { data, error } = await supabase.functions.invoke<ScrapeForumStatsResult & { error?: string }>(
    FUNCTION_NAME,
    { body: payload }
  );

  if (error) {
    if (error instanceof FunctionsFetchError) {
      const inner = unwrapFetchFailure(error.context);
      const msg = inner || error.message;
      if (isEdgeNotDeployedMessage(msg)) {
        throw new Error(formatEdgeNotDeployedError(`Edge (tarayıcı): ${msg}`));
      }
      throw new Error(`Edge (tarayıcı): ${msg}\n${formatEdgeNetworkHint()}`);
    }
    if (error instanceof FunctionsHttpError) {
      const res = error.context as Response;
      const trimmed = (await res.text()).trim();
      if (res.status === 404 && isEdgeNotDeployedMessage(trimmed)) {
        throw new Error(formatEdgeNotDeployedError('Edge (tarayıcı): scrape-forum-stats bulunamadı (404).'));
      }
      let parsed: ScrapeForumStatsResult & { error?: string; edge_deploy_required?: boolean };
      try {
        parsed = trimmed ? JSON.parse(trimmed) : ({} as ScrapeForumStatsResult);
      } catch {
        throw new Error(
          `Edge geçersiz yanıt (HTTP ${res.status}): ${trimmed.slice(0, 280)}${trimmed.length > 280 ? '…' : ''}`
        );
      }
      if (parsed.edge_deploy_required || (res.status === 404 && isEdgeNotDeployedMessage(trimmed))) {
        throw new Error(formatEdgeNotDeployedError(typeof parsed.error === 'string' ? parsed.error : 'Edge deploy gerekli.'));
      }
      if (typeof parsed.error === 'string' && parsed.error) {
        throw new Error(parsed.error);
      }
      throw new Error(`Edge HTTP ${res.status}: ${trimmed.slice(0, 280)}${trimmed.length > 280 ? '…' : ''}`);
    }
    throw new Error((error as Error).message || String(error));
  }

  if (!data || typeof data !== 'object') {
    throw new Error('Edge: JSON yanıt bekleniyordu');
  }
  if (typeof data.error === 'string' && data.error) {
    throw new Error(data.error);
  }
  return finalizeScrapeResult(data);
}

async function scrapeForumStatsViaProxy(
  supabase: SupabaseClient,
  payload: ScrapeForumStatsPayload,
  proxyConfig: string
): Promise<ScrapeForumStatsResult> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Bu işlem için giriş yapmalısınız');
  }
  const anon = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
  if (!anon) {
    throw new Error('VITE_SUPABASE_ANON_KEY tanımlı değil');
  }

  const url = resolveProxyUrl(proxyConfig);
  const sameOrigin = isProxyFetchSameOrigin(url);

  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      mode: sameOrigin ? 'same-origin' : 'cors',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        'X-Supabase-Authorization': `Bearer ${session.access_token}`,
        apikey: anon,
      },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    const inner = unwrapFetchFailure(err);
    throw new Error(
      `Proxy isteği gönderilemedi (${url}). ${inner || 'Failed to fetch'}`.trim() +
        ' — Aynı origin için same-origin modu kullanıldı; hâlâ hata varsa dosya yolu, PHP, reklam engelleyici veya sunucu güvenlik duvarı kontrol edin.'
    );
  }

  const text = await res.text();
  const trimmed = text.trim();

  if (
    trimmed.startsWith('<!DOCTYPE') ||
    trimmed.startsWith('<!doctype') ||
    trimmed.startsWith('<html') ||
    trimmed.startsWith('<HTML')
  ) {
    throw new Error(SCRAPER_PROXY_SKIP);
  }

  if (res.status === 404) {
    throw new Error(SCRAPER_PROXY_SKIP);
  }

  let body: ScrapeForumStatsResult & { error?: string; edge_deploy_required?: boolean };
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    throw new Error(`Proxy geçersiz yanıt (HTTP ${res.status}): ${text.slice(0, 200)}`);
  }

  if (body.edge_deploy_required || (res.status === 503 && isEdgeNotDeployedMessage(body.error ?? text))) {
    throw new Error(formatEdgeNotDeployedError(body.error ?? 'PHP proxy: Edge fonksiyonu yok.'));
  }

  if (!res.ok) {
    throw new Error(body.error || `Proxy HTTP ${res.status}`);
  }
  if (typeof body.error === 'string' && body.error) {
    throw new Error(body.error);
  }
  return finalizeScrapeResult(body);
}

function buildProxyCandidates(): string[] {
  /** Sadece Edge önce: proxy listesi bilinçli kısıtlanır. Geliştirme modunda da kök PHP denenir. */
  if (import.meta.env.VITE_SCRAPE_INVOKE_FIRST === 'true') {
    const only = (import.meta.env.VITE_SCRAPE_FAILOVER_PROXY as string | undefined)?.trim();
    return only ? [only] : [];
  }

  const rawList: string[] = [];
  const fromEnv = (import.meta.env.VITE_SCRAPE_FAILOVER_PROXY as string | undefined)?.trim();
  if (fromEnv) {
    rawList.push(fromEnv);
  }

  // Reklam engelleyiciler "scrape" URL’sini kesebilir — köprü dosyası önce
  rawList.push('/forum-stats-bridge.php');
  rawList.push('forum-stats-bridge.php');
  if (typeof window !== 'undefined') {
    try {
      const pageDir = new URL('.', window.location.href).href;
      rawList.push(new URL('forum-stats-bridge.php', pageDir).href);
    } catch {
      /* ignore */
    }
  }

  rawList.push('/scrape-forum-proxy.php');
  rawList.push('scrape-forum-proxy.php');

  if (typeof window !== 'undefined') {
    try {
      const pageDir = new URL('.', window.location.href).href;
      rawList.push(new URL('scrape-forum-proxy.php', pageDir).href);
    } catch {
      /* ignore */
    }
  }

  const appBase = normalizedAppBasePath();
  if (appBase) {
    rawList.push(`${appBase}forum-stats-bridge.php`.replace(/\/{2,}/g, '/'));
    rawList.push(`${appBase}scrape-forum-proxy.php`.replace(/\/{2,}/g, '/'));
  }

  if (
    typeof window !== 'undefined' &&
    import.meta.env.VITE_SCRAPE_TRY_CROSS_ORIGIN_PROXY === 'true'
  ) {
    const host = window.location.hostname;
    const proto = window.location.protocol || 'https:';
    if (!host.startsWith('www.')) {
      rawList.push(`${proto}//www.${host}/forum-stats-bridge.php`);
      rawList.push(`${proto}//www.${host}/scrape-forum-proxy.php`);
    } else {
      const apex = host.slice(4);
      if (apex) {
        rawList.push(`${proto}//${apex}/forum-stats-bridge.php`);
        rawList.push(`${proto}//${apex}/scrape-forum-proxy.php`);
      }
    }
  }

  if (typeof window === 'undefined') {
    return [...new Set(rawList)];
  }

  const seen = new Set<string>();
  const out: string[] = [];
  for (const r of rawList) {
    const resolved = resolveProxyUrl(r);
    if (!seen.has(resolved)) {
      seen.add(resolved);
      out.push(r);
    }
  }
  return out;
}

async function tryProxyList(
  supabase: SupabaseClient,
  payload: ScrapeForumStatsPayload,
  paths: string[]
): Promise<{ ok: true; data: ScrapeForumStatsResult } | { ok: false; lastError: Error | null }> {
  let lastError: Error | null = null;
  for (const p of paths) {
    try {
      const data = await scrapeForumStatsViaProxy(supabase, payload, p);
      return { ok: true, data };
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      lastError = err;
      if (isRetryableProxyError(err)) {
        continue;
      }
      return { ok: false, lastError: err };
    }
  }
  return { ok: false, lastError };
}

function scrapeFailureChecklist(proxyTried: boolean, lastErrorMessage?: string): string {
  if (lastErrorMessage && isTargetSiteFetchError(lastErrorMessage)) {
    return 'Hedef site Edge ve hosting üzerinden erişimi reddetmiş olabilir (403/WAF/SSL). Manuel güncelleme veya forum sahibi IP izni deneyin. Otomatik PHP yedeği scrape-forum-proxy.php güncel sürümde 422 hatalarında devreye girer.';
  }
  if (!proxyTried) {
    return 'Geliştirme: VITE_SCRAPE_FAILOVER_PROXY ile tam proxy URL.';
  }
  return 'Altyapı: npm run deploy:scrape-edge + sunucuya güncel scrape-forum-proxy.php. İsteğe bağlı: FORUM_SCRAPE_LOCAL_ONLY=1 (yalnızca hosting).';
}

export async function scrapeForumStats(
  supabase: SupabaseClient,
  payload: ScrapeForumStatsPayload
): Promise<ScrapeForumStatsResult> {
  const exclusiveProxy = (import.meta.env.VITE_SUPABASE_FUNCTION_PROXY as string | undefined)?.trim();
  if (exclusiveProxy) {
    return scrapeForumStatsViaProxy(supabase, payload, exclusiveProxy);
  }

  const proxyPaths = buildProxyCandidates();
  const invokeFirst = import.meta.env.VITE_SCRAPE_INVOKE_FIRST === 'true';

  if (invokeFirst) {
    try {
      return await invokeScrapeViaEdgeFetch(supabase, payload);
    } catch (edgeErr) {
      const edgeMsg = edgeErr instanceof Error ? edgeErr.message : String(edgeErr);
      if (proxyPaths.length === 0) {
        throw new Error(formatFinalScrapeFailure(edgeMsg, null, false));
      }
      const via = await tryProxyList(supabase, payload, proxyPaths);
      if (via.ok) {
        return via.data;
      }
      throw new Error(formatFinalScrapeFailure(edgeMsg, via.lastError ?? null, true));
    }
  }

  let proxyLastError: Error | null = null;
  if (proxyPaths.length > 0) {
    const early = await tryProxyList(supabase, payload, proxyPaths);
    if (early.ok) {
      return early.data;
    }
    proxyLastError = early.lastError;
    if (proxyLastError && !isRetryableProxyError(proxyLastError)) {
      throw new Error(`${proxyLastError.message}\n\n${scrapeFailureChecklist(true, proxyLastError.message)}`);
    }
  }

  try {
    return await invokeScrapeViaEdgeFetch(supabase, payload);
  } catch (edgeErr) {
    const edgeMsg = edgeErr instanceof Error ? edgeErr.message : String(edgeErr);
    throw new Error(formatFinalScrapeFailure(edgeMsg, proxyLastError, proxyPaths.length > 0));
  }
}
